import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import {
  DataBlocks,
  I18n,
  Checkbox,
  Button,
  RadioGroup,
  RadioButton,
  TextInput,
  Link,
  ErrorBlock,
  Status,
} from 'components';
import { formatPhone, formatDate } from 'utils/formatString';

import styles from './styles.pcss';

const cx = classNames.bind(styles);

class PeriodicRequest extends PureComponent {
  constructor(props) {
    super(props);

    this.state = this.getDefaultState();
  }

  getDefaultState = () => ({
    numbers: this.props.numberStatus,
    format: this.props.reportType || 'Pdf',
    startDate: this.props.startDate || 1,
  });

  toggleNumber = (toggledNumber) => {
    this.setState((prevState) => ({
      numbers: prevState.numbers.map((number) => (number === toggledNumber ? {
        ...number,
        active: !number.active,
      } : number)),
    }));
  };

  getIsDirty = () => {
    const { numbers, format, startDate } = this.state;

    if (!this.props.email && this.getIsNumbersNotSelected()) {
      return false;
    }

    const currentNumbers = numbers.map((number) => (number.active ? 1 : 0));
    const defaultNumbers = this.getDefaultState().numbers.map((number) => (number.active ? 1 : 0));

    let isCheckboxesChanged = false;
    const isStartDateChanged = startDate !== this.getDefaultState().startDate;
    const isFormatChanged = format !== this.getDefaultState().format;

    currentNumbers.forEach((el, index) => {
      if (el !== defaultNumbers[index]) isCheckboxesChanged = true;
    });

    return (isCheckboxesChanged || isStartDateChanged || isFormatChanged);
  };

  getIsNumbersNotSelected = () => this.state.numbers.every((number) => !number.active);

  getButtonText = () => {
    const { status } = this.props;

    if (status === 'idle') {
      return <I18n path="settings.notifications.periodicRequestRenewSubscription" />;
    }

    if (this.getIsDirty() && this.getIsNumbersNotSelected() && status === 'active') {
      return <I18n path="settings.notifications.periodicRequestCancelSubscription" />;
    }

    return <I18n path="common.save" />;
  };

  onClickAction = async () => {
    const { status, onSave } = this.props;

    await onSave({ ...this.state, status });

    if (this.props.isSuccess) this.setState(this.getDefaultState());
  };

  render() {
    const {
      status,
      email,
      confirmStatus,
      error,
      dueDate,
      onEmailEdit,
      refreshAction,
      isSaving,
    } = this.props;

    const {
      numbers,
      format,
      startDate,
    } = this.state;

    const buttonDisabled = status === 'idle' ? false : !this.getIsDirty();

    if (error) {
      return (
        <ErrorBlock
          title={<I18n path="settings.notifications.periodicRequestTitle" />}
          refreshAction={refreshAction}
        />
      );
    }

    return (
      <DataBlocks.Block
        className={cx('component')}
        title={<I18n path="settings.notifications.periodicRequestTitle" />}
        description={dueDate && (
          <I18n
            path="settings.notifications.periodicRequestEnd"
            vars={{
              date: formatDate(dueDate),
            }}
          />
        )}
        isLoading={!status}
      >
        {status === 'idle' && (
          <Status>
            <I18n path="settings.notifications.periodicRequestIsIdle" />
          </Status>
        )}

        <div className={cx('block')}>
          <div className={cx('blockTitle')}>
            <I18n path={numbers.length === 1
              ? 'settings.notifications.periodicRequestSingleNumber'
              : 'settings.notifications.periodicRequestNumbers'}
            />
          </div>

          <div className={cx('numbers')}>
            {numbers.map((number) => (
              <Checkbox
                key={number.ctn}
                className={cx('number')}
                name={`number-checkbox-${number.ctn}`}
                checked={number.active}
                handleChange={() => this.toggleNumber(number)}
              >
                {formatPhone(number.ctn)}
                {number.nickname && (
                  <span className={cx('nickname')}>
                    {number.nickname}
                  </span>
                )}
              </Checkbox>
            ))}
          </div>
        </div>

        <div className={cx('block')}>
          <div className={cx('blockTitle')}>
            <I18n path="settings.notifications.periodicRequestDay" />
          </div>

          <I18n
            path="settings.notifications.periodicRequestStartInput"
            vars={{
              input: (
                <TextInput
                  key="daysInput"
                  ref={(c) => { this.daysInput = c; }}
                  className={cx('daysInput')}
                  maxLength="2"
                  value={startDate}
                  mask="__"
                  onChange={(value) => this.setState({
                    startDate: value >= 1 && value <= 31 ? +value : 1,
                  })}
                />
              ),
            }}
          />

          <div className={cx('infoMessage')}>
            <I18n
              path="settings.notifications.periodicRequestInterval"
              vars={{
                start: startDate,
                end: startDate > 25 ? startDate - 25 : startDate + 6,
              }}
            />
          </div>
        </div>

        <div className={cx('block')}>
          <div className={cx('blockTitle')}>
            <I18n
              path="settings.notifications.periodicRequestEmail"
              vars={{ email: email || '' }}
            />
          </div>

          <div className={cx('email')}>
            {(email && confirmStatus) ? (
              <Link onClick={onEmailEdit}>
                <I18n path="common.edit" />
              </Link>
            ) : (
              <>
                <I18n path="settings.notifications.periodicRequestNoEmail" />
                <Link onClick={onEmailEdit}>
                  <I18n path="settings.notifications.periodicRequestSetEmail" />
                </Link>
              </>
            )}
          </div>
        </div>

        <div className={cx('block', 'actions')}>
          <Button
            onClick={this.onClickAction}
            isLoading={isSaving}
            disabled={buttonDisabled}
          >
            {this.getButtonText()}
          </Button>

          {this.getIsDirty() && (
            <>
              <I18n
                className={cx('actionsDivider')}
                path="common.or"
              />
              <Link
                className={cx('link')}
                onClick={() => this.setState(this.getDefaultState())}
              >
                <I18n path="common.revertChanges" />
              </Link>
            </>
          )}
        </div>
      </DataBlocks.Block>
    );
  }
}

PeriodicRequest.propTypes = {
  status: PropTypes.string,
  email: PropTypes.string,
  confirmStatus: PropTypes.bool,
  dueDate: PropTypes.string,
  error: PropTypes.string,
  refreshAction: PropTypes.func,
  onSave: PropTypes.func,
  onEmailEdit: PropTypes.func,
  isSaving: PropTypes.bool,
  reportType: PropTypes.string,
  numberStatus: PropTypes.arrayOf(
    PropTypes.shape({
      ctn: PropTypes.string,
      active: PropTypes.bool,
      available: PropTypes.bool,
      nickname: PropTypes.string,
    }),
  ),
  startDate: PropTypes.number,
  isSuccess: PropTypes.bool,
};

export default PeriodicRequest;
